import { ChangeEvent, KeyboardEvent, ReactElement, ReactNode, useState } from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography
} from '@mui/material';
import Icon from 'components/icons/Icon';
import { IconType } from '../../../@types/icons';
import { Claim, TenantCapabilities } from '../../../@types/organization';
import { useTranslator } from 'translation/useTranslator';
import { ITranslator } from 'translation/generated/translationInterface';
import { TextField } from '@nps.today/component-library';
import useAuth from 'hooks/useAuth';
import {
  AccessLevel,
  Cell,
  holdsPermissionGuard,
  isGlobalClaimValue,
  isPermissionLocked,
  isPermissionVisible,
  moduleIconFor,
  moduleRows,
  resolveDescription,
  resolveLabel,
  SegmentedOption,
  standaloneIconFor,
  standaloneList,
  withImplicitPermissionClaims
} from './permissions.helpers';

const SHIELD_COLOR = 'error.main';

type OrganizationUserPermissionsProps = {
  selectedClaims: Claim[];
  isEditable: boolean;
  canAssignClaims: boolean;
  capabilities: TenantCapabilities;
  onClaimsChange: (updatedClaims: Claim[]) => void;
  headerSlot?: ReactNode;
  initialClaims?: Claim[];
};

const segmentedGroupSx = {
  position: 'relative',
  display: 'inline-grid',
  gridAutoFlow: 'column',
  gridAutoColumns: '1fr',
  alignSelf: 'flex-start',
  p: '3px',
  borderRadius: '10px',
  bgcolor: 'background.neutral'
} as const;

const segmentedThumbSx = (optionCount: number, selectedIndex: number): SxProps<Theme> => ({
  position: 'absolute',
  top: '3px',
  bottom: '3px',
  left: '3px',
  width: `calc((100% - 6px) / ${optionCount})`,
  borderRadius: '7px',
  bgcolor: 'background.paper',
  boxShadow: 1,
  opacity: selectedIndex === -1 ? 0 : 1,
  transform: `translateX(${Math.max(selectedIndex, 0) * 100}%)`,
  transition: (theme) => theme.transitions.create(['transform', 'opacity'], { duration: 200 })
});

const segmentedButtonSx = (active: boolean): SxProps<Theme> => ({
  position: 'relative',
  px: 1.5,
  height: 26,
  borderRadius: '7px',
  typography: 'caption',
  fontWeight: 600,
  color: active ? 'text.primary' : 'text.secondary',
  transition: (theme) => theme.transitions.create('color', { duration: 200 })
});

const SegmentedControl = ({
  options,
  value,
  onChange
}: {
  options: SegmentedOption[];
  value: AccessLevel | null;
  onChange: (next: AccessLevel) => void;
}) => {
  const selectedIndex = options.findIndex((option) => option.value === value);
  return (
    <Box role="group" sx={segmentedGroupSx}>
      <Box sx={segmentedThumbSx(options.length, selectedIndex)} />
      {options.map((option) => (
        <ButtonBase
          key={option.value}
          onClick={() => {
            if (option.value !== value) {
              onChange(option.value);
            }
          }}
          aria-pressed={option.value === value}
          sx={segmentedButtonSx(option.value === value)}
        >
          {option.label}
        </ButtonBase>
      ))}
    </Box>
  );
};

const GlobalClaimBadge = ({ title }: { title: string }) => (
  <Tooltip title={title} arrow>
    <Icon type={IconType.shieldfilled} sx={{ ml: 0.5, color: SHIELD_COLOR, fontSize: 18 }} />
  </Tooltip>
);

const permissionTileSx = {
  p: 2,
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5
} as const;

const iconTileSx = {
  width: 40,
  height: 40,
  borderRadius: '12px',
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'primary.lighter',
  color: 'primary.darker'
} as const;

const PermissionTileHeader = ({
  icon,
  translationKey,
  fallbackLabel,
  isGlobal
}: {
  icon: IconType;
  translationKey: string;
  fallbackLabel: string;
  isGlobal: boolean;
}) => {
  const translator = useTranslator();
  const description = resolveDescription(translator, translationKey);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={iconTileSx}>
        <Icon type={icon} fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
            {resolveLabel(translator, translationKey, fallbackLabel)}
          </Typography>
          {isGlobal && <GlobalClaimBadge title={translator.permissionGlobalIndicator()} />}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const chipIconFor = (cell: Cell, translator: ITranslator): ReactElement | undefined => {
  if (cell.locked) {
    return <Icon type={IconType.userlock} />;
  }
  if (cell.impliedRead) {
    return <Icon type={IconType.security} />;
  }
  if (cell.isGlobalCell) {
    return (
      <Tooltip title={translator.permissionGlobalIndicator()} arrow>
        <Icon type={IconType.shieldfilled} />
      </Tooltip>
    );
  }
  return undefined;
};

const chipOpacity = (cell: Cell): number => {
  if (cell.locked) {
    return 0.55;
  }
  if (cell.impliedRead) {
    return 0.85;
  }
  return 1;
};

const chipSx = (cell: Cell, interactive: boolean): SxProps<Theme> => ({
  fontWeight: 600,
  border: cell.checked ? 'none' : '1px solid',
  borderColor: 'divider',
  color:
    (!interactive || (cell.locked && !cell.lockedRemovable)) && !cell.checked
      ? 'text.disabled'
      : undefined,
  cursor:
    interactive && !cell.impliedRead && (!cell.locked || cell.lockedRemovable)
      ? 'pointer'
      : 'default',
  opacity: chipOpacity(cell),
  '& .MuiChip-icon': {
    fontSize: 16,
    marginLeft: '6px',
    marginRight: '-2px',
    ...(cell.isGlobalCell && !cell.locked ? { color: SHIELD_COLOR } : {})
  }
});

const PermissionChip = ({
  cell,
  interactive,
  onToggle
}: {
  cell: Cell;
  interactive: boolean;
  onToggle: () => void;
}) => {
  const translator = useTranslator();
  const clickable = interactive && !cell.impliedRead && (!cell.locked || cell.lockedRemovable);
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };
  const stateLabel = cell.checked ? translator.enabled() : translator.disabled();
  const chip = (
    <Chip
      size="small"
      label={cell.label}
      icon={chipIconFor(cell, translator)}
      variant={cell.checked ? 'filled' : 'outlined'}
      color={cell.checked ? 'primary' : 'default'}
      onClick={clickable ? onToggle : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
      aria-pressed={clickable ? cell.checked : undefined}
      aria-label={clickable ? undefined : `${cell.label}: ${stateLabel}`}
      sx={chipSx(cell, interactive)}
    />
  );
  if (cell.locked) {
    return (
      <Tooltip title={translator.permissionLockedByOrder()} arrow>
        <span>{chip}</span>
      </Tooltip>
    );
  }
  if (!cell.impliedRead) {
    return chip;
  }
  return (
    <Tooltip title={translator.permissionReadImpliedByWrite()} arrow>
      <span>{chip}</span>
    </Tooltip>
  );
};

const OrganizationUserPermissions = ({
  selectedClaims,
  isEditable,
  canAssignClaims,
  capabilities,
  onClaimsChange,
  headerSlot,
  initialClaims
}: OrganizationUserPermissionsProps) => {
  const translator = useTranslator();
  const [searchText, setSearchText] = useState('');

  const { user } = useAuth();
  const heldClaims = user?.claims ?? [];

  const permissionValues = new Set(
    (selectedClaims ?? [])
      .filter((c: Claim) => c.claimType === 'permissions')
      .map((c: Claim) => c.claimValue)
  );
  const initialPermissionValues = new Set(
    (initialClaims ?? [])
      .filter((c: Claim) => c.claimType === 'permissions')
      .map((c: Claim) => c.claimValue)
  );

  const hasClaim = (value: string) => permissionValues.has(value);
  const isLocked = (value: string) => isPermissionLocked(value, capabilities);
  const isLockedRemovable = (value: string) =>
    isLocked(value) && initialPermissionValues.has(value);

  const isClaimVisible = (claimValue: string): boolean =>
    isPermissionVisible(claimValue, heldClaims, capabilities) ||
    ((hasClaim(claimValue) || initialPermissionValues.has(claimValue)) &&
      holdsPermissionGuard(claimValue, heldClaims));
  const visibleModuleRows = moduleRows
    .map((module) => ({
      ...module,
      opsMap: new Map(
        Array.from(module.opsMap.entries()).filter(([, claimValue]) => isClaimVisible(claimValue))
      )
    }))
    .filter((module) => module.opsMap.size > 0);
  const visibleStandaloneList = standaloneList.filter((perm) => isClaimVisible(perm.value));

  const CRUD_COLUMNS: { key: string; label: string }[] = [
    { key: 'c', label: translator.create() },
    { key: 'r', label: translator.view() },
    { key: 'u', label: translator.edit() },
    { key: 'd', label: translator.delete() }
  ];

  const toggleClaim = (value: string) => {
    if (isLocked(value) && !isLockedRemovable(value)) {
      return;
    }
    const existing = selectedClaims.filter(
      (c: Claim) => !(c.claimType === 'permissions' && c.claimValue === value)
    );
    if (!hasClaim(value)) {
      existing.push({ claimType: 'permissions', claimValue: value });
    }
    onClaimsChange(withImplicitPermissionClaims(existing));
  };

  const applyLevel = (opsMap: Map<string, string>, level: AccessLevel) => {
    const manageableClaimValues = new Set(
      Array.from(opsMap.values()).filter((claimValue) => !isLocked(claimValue))
    );
    const updated = selectedClaims.filter(
      (c: Claim) => !(c.claimType === 'permissions' && manageableClaimValues.has(c.claimValue))
    );
    if (level === 'view') {
      const readClaim = opsMap.get('r');
      if (readClaim && manageableClaimValues.has(readClaim)) {
        updated.push({ claimType: 'permissions', claimValue: readClaim });
      }
    } else if (level === 'full') {
      manageableClaimValues.forEach((claimValue) =>
        updated.push({ claimType: 'permissions', claimValue })
      );
    }
    onClaimsChange(withImplicitPermissionClaims(updated));
  };

  const deriveLevel = (opsMap: Map<string, string>): AccessLevel => {
    const distinctClaims = Array.from(new Set(opsMap.values())).filter(
      (claimValue) => !isLocked(claimValue)
    );
    const selected = distinctClaims.filter((claimValue) => hasClaim(claimValue));
    if (selected.length === 0) {
      return 'none';
    }
    if (selected.length === distinctClaims.length) {
      return 'full';
    }
    const readClaim = opsMap.get('r');
    if (readClaim && selected.length === 1 && selected[0] === readClaim) {
      return 'view';
    }
    return 'custom';
  };

  const buildCells = (opsMap: Map<string, string>, isGlobalModule: boolean): Cell[] => {
    const hasAnyWrite = ['c', 'u', 'd'].some((opKey) => {
      const opClaim = opsMap.get(opKey);
      return opClaim ? hasClaim(opClaim) && !isLocked(opClaim) : false;
    });
    return CRUD_COLUMNS.filter((col) => opsMap.has(col.key)).map((col) => {
      const claimValue = opsMap.get(col.key)!;
      const explicitlyChecked = hasClaim(claimValue);
      const impliedRead = col.key === 'r' && hasAnyWrite && !explicitlyChecked;
      return {
        key: col.key,
        label: col.label,
        claimValue,
        explicitlyChecked,
        impliedRead,
        checked: explicitlyChecked || impliedRead,
        isGlobalCell: !isGlobalModule && isGlobalClaimValue(claimValue),
        locked: isLocked(claimValue),
        lockedRemovable: isLockedRemovable(claimValue)
      };
    });
  };

  const toEnabledCell = (claimValue: string): Cell => ({
    key: claimValue,
    label: translator.enabled(),
    claimValue,
    explicitlyChecked: hasClaim(claimValue),
    impliedRead: false,
    checked: hasClaim(claimValue),
    isGlobalCell: false,
    locked: isLocked(claimValue),
    lockedRemovable: isLockedRemovable(claimValue)
  });

  const interactive = isEditable && canAssignClaims;

  const grantedModuleRows = visibleModuleRows.filter((module) =>
    Array.from(new Set(module.opsMap.values())).some((claimValue) => hasClaim(claimValue))
  );
  const grantedStandaloneList = visibleStandaloneList.filter((perm) => hasClaim(perm.value));
  const baseModuleRows = interactive ? visibleModuleRows : grantedModuleRows;
  const baseStandaloneList = interactive ? visibleStandaloneList : grantedStandaloneList;

  const normalizedSearch = searchText.trim().toLowerCase();
  const matchesSearch = (translationKey: string, fallbackLabel: string): boolean => {
    if (!normalizedSearch) {
      return true;
    }
    const label = resolveLabel(translator, translationKey, fallbackLabel, false);
    const description = resolveDescription(translator, translationKey);
    return (
      label.toLowerCase().includes(normalizedSearch) ||
      description.toLowerCase().includes(normalizedSearch)
    );
  };
  const displayedModuleRows = baseModuleRows.filter((module) =>
    matchesSearch(module.translationKey, module.fallbackLabel)
  );
  const displayedStandaloneList = baseStandaloneList.filter((perm) =>
    matchesSearch(perm.translationKey, perm.fallbackLabel)
  );
  const hiddenWithoutAccessCount = interactive
    ? 0
    : visibleModuleRows.length -
      grantedModuleRows.length +
      (visibleStandaloneList.length - grantedStandaloneList.length);
  if (visibleModuleRows.length === 0 && visibleStandaloneList.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', containerType: 'inline-size' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 2,
          alignItems: 'center',
          mb: 2,
          ...(headerSlot && {
            '@container (min-width: 640px)': {
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
            }
          })
        }}
      >
        <TextField
          dataTestId="organization-user-permissions-search"
          fullWidth
          placeholder={translator.searchPermissions()}
          value={searchText}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchText(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon type={IconType.search} fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={translator.clear()}
                  onClick={() => setSearchText('')}
                >
                  <Icon type={IconType.clear} fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }}
        />
        {headerSlot}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 2,
          '@container (min-width: 640px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
          },
          '@container (min-width: 960px)': {
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))'
          }
        }}
      >
        {displayedModuleRows.map((module) => {
          const distinctClaimValues = Array.from(new Set(module.opsMap.values()));
          const isGlobalModule =
            distinctClaimValues.length > 0 && distinctClaimValues.every(isGlobalClaimValue);
          const level = deriveLevel(module.opsMap);
          const moduleIcon = moduleIconFor(module.prefix);
          const hasMultipleClaims = distinctClaimValues.length > 1;
          const cells = hasMultipleClaims
            ? buildCells(module.opsMap, isGlobalModule)
            : distinctClaimValues.map((claimValue) => toEnabledCell(claimValue));
          const hasDistinctViewLevel = module.opsMap.has('r') && hasMultipleClaims;
          const levelOptions: SegmentedOption[] = [
            { value: 'none', label: translator.none() },
            ...(hasDistinctViewLevel ? [{ value: 'view' as const, label: translator.view() }] : []),
            { value: 'full', label: translator.all() }
          ];

          const hasManageableClaims = distinctClaimValues.some(
            (claimValue) => !isLocked(claimValue)
          );

          return (
            <Paper key={module.prefix} variant="outlined" sx={permissionTileSx}>
              <PermissionTileHeader
                icon={moduleIcon}
                translationKey={module.translationKey}
                fallbackLabel={module.fallbackLabel}
                isGlobal={isGlobalModule}
              />

              {interactive && hasMultipleClaims && hasManageableClaims && (
                <SegmentedControl
                  options={levelOptions}
                  value={level === 'custom' ? null : level}
                  onChange={(next) => applyLevel(module.opsMap, next)}
                />
              )}

              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 'auto' }}>
                {cells.map((cell) => (
                  <PermissionChip
                    key={cell.key}
                    cell={cell}
                    interactive={interactive}
                    onToggle={() => toggleClaim(cell.claimValue)}
                  />
                ))}
              </Stack>
            </Paper>
          );
        })}
        {displayedStandaloneList.map((perm) => {
          const cell = toEnabledCell(perm.value);
          return (
            <Paper key={perm.value} variant="outlined" sx={permissionTileSx}>
              <PermissionTileHeader
                icon={standaloneIconFor(perm.value)}
                translationKey={perm.translationKey}
                fallbackLabel={perm.fallbackLabel}
                isGlobal={isGlobalClaimValue(perm.value)}
              />
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 'auto' }}>
                <PermissionChip
                  cell={cell}
                  interactive={interactive}
                  onToggle={() => toggleClaim(perm.value)}
                />
              </Stack>
            </Paper>
          );
        })}
      </Box>

      {displayedModuleRows.length === 0 && displayedStandaloneList.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {normalizedSearch
            ? translator.noPermissionsMatchSearch()
            : translator.noPermissionsAssigned()}
        </Typography>
      )}

      {hiddenWithoutAccessCount > 0 && !normalizedSearch && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {translator.permissionsWithoutAccessHidden(`${hiddenWithoutAccessCount}`)}
        </Typography>
      )}
    </Box>
  );
};

export default OrganizationUserPermissions;
